import {
    useCallback,
    useMemo,
    useState,
} from 'react'
import {
    FormPart,
    ValidationResult,
} from 'app/types/form.types'
import {
    BatchEntity,
    WarningCardVariant,
} from 'app/enums/common.enums'
import {
    RequestConfigFunction,
} from 'app/types/request.types'
import WarningCard from 'app/shared-components/WarningCard'

import useStyles from '../BatchActions.shared.styles'
import UploadFeedback from '../components/UploadFeedback'
import {
    FeedbackFormData,
    UploadFeedbackSpecificProps,
} from '../components/UploadFeedback/UploadFeedback.types'
import useBatchUpload from '../hooks/useBatchUpload/useBatchUpload'

import {
    BatchOperationPayload,
} from '../hooks/useBatchUpload'
import CsvDataLoader from './CsvDataLoader'

enum Step {
    Loader = 'loader',
    Feedback = 'feedback',
}

type Props<
    RawData extends Record<string, any>,
    ParsedData = Record<string, any>
> = UploadFeedbackSpecificProps<ParsedData> & {
    /** Target entity for bulk upload operation */
    entity: BatchEntity
    /** Function that maps record data to upload request configuration */
    uploadParamsMapper: RequestConfigFunction<BatchOperationPayload<ParsedData>>
    /** Function to parse data extracted from file.
     *  Triggered when file passes validation */
    parseDataOnLoad?: (data: RawData[]) => Promise<Record<string, any>[]> | Record<string, any>[]
    /** Function triggered when user click Cancel */
    onCancel: () => void
    /** Function to perform additional validation of data extracted from file */
    validateData?: (data: RawData[]) => ValidationResult
    /** List of mandatory headers that must be present in the file */
    mandatoryHeaders?: Extract<keyof RawData, string>[]
}

const CsvUpload = <RawData extends Record<string, any>, ParsedData = Record<string, any>>({
    parseDataOnLoad,
    onCancel,
    validateData,
    mandatoryHeaders,
    feedbackFormFields,
    errorFeedbackColumns,
    uniqField,
    entity,
    uploadParamsMapper,
}: Readonly<Props<RawData, ParsedData>>) => {
    const {
        classes,
    } = useStyles()
    const [
        visibleStep,
        setVisibleStep,
    ] = useState<Step>(Step.Loader)
    const [
        loadedData,
        setLoadedData,
    ] = useState<Record<string, any>[]>([])

    const {
        uploadRecords,
        stopUploadRecords,
        failedUploads,
        successfulUploads,
        isLoading: isUploadingRecords,
    } = useBatchUpload({
        uploadParamsMapper,
        plainData: loadedData,
        entity,
    })

    const handleLoadData = useCallback(async (data: Record<string, any>[]) => {
        const parsedData = parseDataOnLoad ? await parseDataOnLoad(data as RawData[]) : data

        setLoadedData(parsedData)
    }, [parseDataOnLoad])

    const handleContinue = useCallback(() => {
        setVisibleStep(Step.Feedback)
        uploadRecords()
    }, [uploadRecords])

    const feedbackFormValue: FeedbackFormData = useMemo(() => {
        return {
            selectedRecords: loadedData.length,
            updatedRecords: successfulUploads,
            failedRecords: failedUploads.length,
        }
    }, [
        failedUploads.length,
        loadedData.length,
        successfulUploads,
    ])

    const renderFeedbackFinalMessage = useCallback(({
        isStoppedByUser,
    }) => {
        if (isStoppedByUser) {
            return (
                <WarningCard variant={WarningCardVariant.Warning}>
                    <span className={classes.bold}>IMPORTANT: </span>
                    The upload was stopped by user.
                </WarningCard>
            )
        }
        return (
            <WarningCard variant={WarningCardVariant.Success}>
                <span className={classes.bold}>IMPORTANT: </span>
                The upload is successfully completed.
            </WarningCard>

        )
    }, [classes.bold])

    return (
        <div className={classes.container}>
            {visibleStep === Step.Loader && (
                <CsvDataLoader
                    onLoadData={handleLoadData}
                    mandatoryHeaders={mandatoryHeaders}
                    onContinue={handleContinue}
                    onCancel={onCancel}
                    validateData={validateData}
                />
            )}
            {visibleStep === Step.Feedback && (
                <UploadFeedback
                    uniqField={uniqField}
                    columns={errorFeedbackColumns}
                    formFields={feedbackFormFields as FormPart<keyof FeedbackFormData>[]}
                    failedUploads={failedUploads}
                    feedbackFormValue={feedbackFormValue}
                    isLoading={isUploadingRecords}
                    onStopUpdate={stopUploadRecords}
                    onDoneClick={onCancel}
                    renderCustomFinalMessage={renderFeedbackFinalMessage}
                />
            )}
        </div>
    )
}

export default CsvUpload
